import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Target, Clock, ChevronRight, HandCoins } from 'lucide-react'
import { TeamCrest } from './TeamCrest'
import { PlayedBadge } from './PlayedBadge'
import { RegisterBetModal } from './RegisterBetModal'
import type { ChallengeSummary } from '@/types/strategies'

function formatWeekRange(startIso: string | null, endIso: string | null): string {
  if (!startIso || !endIso) return ''
  const fmt = (iso: string) =>
    new Date(`${iso}T00:00:00`).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' }).toUpperCase().replace('.', '')
  return `${fmt(startIso)} — ${fmt(endIso)}`
}

/** P0.31 (26/08/2026, rediseño Mini App -- Sección 5 del encargo): BUG REAL corregido --
 * esta tarjeta consultaba `/challenge/current-week` (el Reto PERSONAL de este usuario), así
 * que si el cliente nunca configuró su propio Reto, mostraba "Configura tu Reto" aunque el
 * Reto Oficial John Anthony (público, `mini_app_user_id IS NULL`) estuviera ACTIVE con banca
 * real. Ahora recibe directamente el `ChallengeSummary` OFICIAL (misma fuente y misma
 * prioridad de selección que `ChallengePage.tsx`: `status === 'ACTIVE'` primero, luego el
 * más reciente -- garantiza que Inicio y Reto SIEMPRE muestren el mismo Reto, nunca ofrece
 * "crear otro" porque el Oficial es siempre auto-creado por el sistema, nunca configurado
 * por el cliente. Si de verdad no existe ninguno todavía (caso límite: el job diario aún no
 * corrió hoy), se muestra un estado de espera honesto, nunca un formulario de alta. */
export function ChallengeHomeCard({ challenge }: { challenge: ChallengeSummary | null }) {
  const [showPlayModal, setShowPlayModal] = useState(false)

  if (!challenge) {
    return (
      <section className="space-y-2">
        <div className="card p-5 rounded-2xl border-yellow-500/30 bg-gradient-to-br from-yellow-950/20 via-panel to-panel text-center space-y-2">
          <Target className="h-7 w-7 mx-auto text-yellow-500/70" />
          <h3 className="text-sm font-black text-text">🎯 RETO JOHN ANTHONY</h3>
          <p className="text-xs text-text-dim leading-relaxed">
            Todavía no hay un Reto Oficial para esta semana. Se crea automáticamente en cuanto haya actividad.
          </p>
        </div>
      </section>
    )
  }

  const weekRange = formatWeekRange(challenge.week_start_date, challenge.week_end_date)
  const isTerminal = challenge.status === 'COMPLETED' || challenge.status === 'FAILED'

  return (
    <section className="space-y-2">
      <div className="card p-5 rounded-2xl border-yellow-500/40 bg-gradient-to-br from-yellow-950/30 via-panel to-panel space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-black text-text flex items-center gap-1.5">🎯 RETO JOHN ANTHONY</h3>
            {weekRange && <p className="text-[9px] font-bold uppercase tracking-wider text-text-dim mt-0.5">Semana actual · {weekRange}</p>}
          </div>
          {isTerminal && (
            <span className="text-[9px] px-2 py-0.5 rounded-full bg-panel-elevated text-text border border-border/50 font-bold uppercase">
              {challenge.status === 'COMPLETED' ? 'Completado' : 'Finalizado'}
            </span>
          )}
        </div>

        <div className="grid grid-cols-3 gap-2.5 text-center">
          <div className="bg-panel-elevated/50 p-2.5 rounded-xl border border-border/30">
            <p className="text-[9px] font-bold uppercase tracking-wider text-text-dim">Capital inicial</p>
            <p className="text-sm font-mono font-black text-text">{challenge.bankroll_initial.toFixed(2)}€</p>
          </div>
          <div className="bg-panel-elevated/50 p-2.5 rounded-xl border border-border/30">
            <p className="text-[9px] font-bold uppercase tracking-wider text-text-dim">Banca actual</p>
            <p className="text-sm font-mono font-black text-yellow-400">{challenge.current_bankroll.toFixed(2)}€</p>
          </div>
          <div className="bg-panel-elevated/50 p-2.5 rounded-xl border border-border/30">
            <p className="text-[9px] font-bold uppercase tracking-wider text-text-dim">Picks</p>
            <p className="text-sm font-mono font-black text-text">{challenge.current_step}</p>
          </div>
        </div>

        {!isTerminal && (
          <div className="bg-panel-elevated/40 p-3 rounded-xl">
            {challenge.has_open_opportunity ? (
              <div className="space-y-2">
                <p className="text-[10px] font-black uppercase text-brand flex items-center gap-1.5">🟢 Oportunidad del día</p>
                {(challenge.last_pick?.home_team || challenge.last_pick?.away_team) && (
                  <div className="flex items-center justify-center gap-3 py-1">
                    <div className="flex flex-col items-center gap-1 w-16 text-center">
                      <TeamCrest src={challenge.last_pick?.home_team_logo} alt={challenge.last_pick?.home_team ?? 'Local'} size={36} />
                      <span className="text-[9px] font-bold text-text line-clamp-1">{challenge.last_pick?.home_team ?? 'Local'}</span>
                    </div>
                    <span className="text-[9px] font-mono font-black text-text-dim">VS</span>
                    <div className="flex flex-col items-center gap-1 w-16 text-center">
                      <TeamCrest src={challenge.last_pick?.away_team_logo} alt={challenge.last_pick?.away_team ?? 'Visitante'} size={36} />
                      <span className="text-[9px] font-bold text-text line-clamp-1">{challenge.last_pick?.away_team ?? 'Visitante'}</span>
                    </div>
                  </div>
                )}
                <p className="text-[11px] text-text text-center font-bold">
                  {challenge.last_pick?.display_selection ?? `${challenge.last_pick?.market} / ${challenge.last_pick?.selection}`}
                </p>
                {challenge.last_pick?.stake_pct_used != null && challenge.last_pick.exposure != null && (
                  <p className="text-[10px] text-yellow-400 text-center font-bold">
                    Stake {challenge.last_pick.exposure.toFixed(2)}€ ({(challenge.last_pick.stake_pct_used * 100).toFixed(0)}%)
                  </p>
                )}
                {challenge.last_pick?.step_id != null && (
                  challenge.last_pick.my_bet ? (
                    <PlayedBadge bet={challenge.last_pick.my_bet} />
                  ) : (
                    <button
                      onClick={() => setShowPlayModal(true)}
                      className="w-full flex items-center justify-center gap-1.5 py-2 rounded-xl bg-panel-elevated border border-border/60 text-text-dim hover:text-yellow-400 hover:border-yellow-500/40 text-[10px] font-black uppercase tracking-wider transition"
                    >
                      <HandCoins className="h-3.5 w-3.5" />
                      La jugué
                    </button>
                  )
                )}
              </div>
            ) : (
              <div className="flex items-start gap-2">
                <Clock className="h-3.5 w-3.5 text-text-dim shrink-0 mt-0.5" />
                <p className="text-[11px] text-text-dim leading-relaxed">
                  <span className="font-black text-yellow-500 uppercase">🟡 Buscando oportunidad. </span>
                  {challenge.no_bet_reason ?? 'John Anthony todavía no encontró hoy una selección que cumpla los criterios del Reto.'}
                </p>
              </div>
            )}
          </div>
        )}

        <Link
          to="/reto"
          className="flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-yellow-500/15 border border-yellow-500/40 text-yellow-400 text-xs font-black uppercase tracking-wider hover:bg-yellow-500/25 transition"
        >
          Ver Reto
          <ChevronRight className="h-3.5 w-3.5" />
        </Link>
      </div>

      {showPlayModal && challenge.last_pick?.step_id != null && (
        <RegisterBetModal
          onClose={() => setShowPlayModal(false)}
          category="challenge"
          matchLabel={`${challenge.last_pick.home_team ?? 'Local'} vs ${challenge.last_pick.away_team ?? 'Visitante'}`}
          selectionLabel={challenge.last_pick.display_selection ?? `${challenge.last_pick.market} / ${challenge.last_pick.selection}`}
          referenceOdds={challenge.last_pick.odds ?? 0}
          challengeStepId={challenge.last_pick.step_id}
        />
      )}
    </section>
  )
}
